import {
	ActionIcon,
	Badge,
	Box,
	Button,
	Card,
	Flex,
	Group,
	Modal,
	Select,
	Stack,
	Text,
	TextInput,
	Textarea,
	Tooltip,
} from '@mantine/core'
import {
	IconChevronLeft,
	IconChevronRight,
	IconPencil,
	IconRefresh,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
	getSpeakingTopicsOfDate,
	regenerateSpeakingTopics,
	SpeakingDailyTopic,
	SpeakingTopicEditPayload,
	SpeakingTopicsOfDate,
	unwrapObject,
	updateSpeakingTopic,
} from 'src/apis/courseAiAPI'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { color } from 'src/variable/constant'
import SpeakingLessonFields, {
	inputsToQuestions,
	questionsError,
	questionsToInputs,
	SpeakingQuestionInput,
} from './SpeakingLessonFields'
import {
	categoryLabel,
	DATE_FORMAT,
	editBlockedReason,
	IeltsFormValues,
	ieltsFormErrors,
	ieltsFormFromTopic,
	ieltsFormToPayload,
	isDateText,
	regenerateBlockedReason,
	shiftDate,
	SPEAKING_CATEGORY_ORDER,
} from './speakingDisplay'

const CATEGORY_COLORS: Record<string, string> = {
	MANUAL: 'green',
	HOT_TOPICS: 'orange',
	IELTS_TOPICS: 'violet',
}

const HOT_LEVELS = [
	{ value: 'A1', label: 'A1' },
	{ value: 'A2', label: 'A2' },
	{ value: 'B1', label: 'B1' },
]
const BASIC_LEVELS = [...HOT_LEVELS, { value: 'B2', label: 'B2' }]

// ---------------------------------------------------------------------------
// One set
// ---------------------------------------------------------------------------

function TopicCard({
	topic,
	onEdit,
	onRegenerate,
}: {
	topic: SpeakingDailyTopic
	onEdit: (topic: SpeakingDailyTopic) => void
	onRegenerate: (topic: SpeakingDailyTopic) => void
}) {
	const editBlocked = editBlockedReason(topic)
	const regenBlocked = regenerateBlockedReason(topic)
	const ielts = topic.ielts

	return (
		<Card
			withBorder
			radius="md"
			padding="md"
			data-testid={`topic-${topic.category}`}
		>
			<Stack spacing={8}>
				<Flex justify="space-between" align="flex-start" gap={8} wrap="wrap">
					<Stack spacing={4} sx={{ minWidth: 0, flex: 1 }}>
						<Group spacing={6}>
							<Badge
								color={CATEGORY_COLORS[topic.category] || 'gray'}
								variant="filled"
							>
								{categoryLabel(topic.category)}
							</Badge>
							{topic.level ? (
								<Badge variant="light" color="blue">
									{topic.level}
								</Badge>
							) : null}
							<Badge variant="light" color="gray">
								{topic.minutes} min
							</Badge>
							<Badge
								variant="light"
								color={topic.started > 0 ? 'red' : 'gray'}
								title="Learners who started a talk on this topic"
							>
								{topic.started} started
							</Badge>
						</Group>
						<Text fw={700} size="lg" sx={{ lineHeight: 1.25 }}>
							{topic.title}
						</Text>
					</Stack>
					<Group spacing={6} noWrap>
						<Tooltip
							label={editBlocked || 'Edit the text of this topic'}
							withinPortal
						>
							<span>
								<Button
									size="xs"
									variant="light"
									color="green"
									leftIcon={<IconPencil size={14} />}
									disabled={Boolean(editBlocked)}
									onClick={() => onEdit(topic)}
								>
									Edit
								</Button>
							</span>
						</Tooltip>
						{topic.category !== 'MANUAL' ? (
							<Tooltip
								label={regenBlocked || 'Write this set again with the AI'}
								withinPortal
							>
								<span>
									<Button
										size="xs"
										variant="light"
										color="orange"
										leftIcon={<IconRefresh size={14} />}
										disabled={Boolean(regenBlocked)}
										onClick={() => onRegenerate(topic)}
									>
										Regenerate
									</Button>
								</span>
							</Tooltip>
						) : null}
					</Group>
				</Flex>

				{topic.intro ? (
					<Text size="sm" color="dimmed">
						{topic.intro}
					</Text>
				) : null}

				<Stack spacing={2}>
					<Text size="xs" fw={700} color="dimmed" tt="uppercase">
						{topic.category === 'IELTS_TOPICS'
							? 'Part 1 questions'
							: 'Questions'}
					</Text>
					{topic.questions.map((question, index) => (
						<Text key={index} size="sm">
							{index + 1}. {question.text}
							{question.hint ? (
								<Text component="span" color="dimmed">
									{' '}
									(hint: {question.hint})
								</Text>
							) : null}
						</Text>
					))}
				</Stack>

				{ielts ? (
					<Stack spacing={8}>
						<Text size="sm">
							<strong>Part 1 theme:</strong> {ielts.part1_theme}
						</Text>
						<Box
							data-testid="cue-card"
							sx={{
								border: '1px solid #c4b5fd',
								background: '#f5f3ff',
								borderRadius: 8,
								padding: '10px 12px',
							}}
						>
							<Text size="xs" fw={700} color="dimmed" tt="uppercase" mb={4}>
								Part 2 cue card ({ielts.part2?.prep_seconds ?? 60} s to prepare,
								up to {ielts.part2?.talk_seconds ?? 120} s to talk)
							</Text>
							<Text size="sm" fw={600}>
								{ielts.part2?.cue_card}
							</Text>
							<Text size="sm" mt={4}>
								You should say:
							</Text>
							<Box component="ul" sx={{ margin: '2px 0 4px', paddingLeft: 20 }}>
								{(ielts.part2?.bullets || []).map((bullet, index) => (
									<li key={index}>
										<Text size="sm">{bullet}</Text>
									</li>
								))}
							</Box>
							<Text size="sm">{ielts.part2?.explain}</Text>
						</Box>
						<Stack spacing={2}>
							<Text size="xs" fw={700} color="dimmed" tt="uppercase">
								Rounding off
							</Text>
							{(ielts.rounding_off || []).map((question, index) => (
								<Text key={index} size="sm">
									{index + 1}. {question}
								</Text>
							))}
						</Stack>
					</Stack>
				) : null}

				{topic.sources.length > 0 ? (
					<Stack spacing={2} data-testid="topic-sources">
						<Text size="xs" fw={700} color="dimmed" tt="uppercase">
							Sources ({topic.sources.length})
						</Text>
						{topic.sources.map((source, index) => (
							<Text key={index} size="xs">
								{source.uri ? (
									<a
										href={source.uri}
										target="_blank"
										rel="noreferrer noopener"
									>
										{source.title || source.uri}
									</a>
								) : (
									source.title
								)}
							</Text>
						))}
					</Stack>
				) : null}

				{topic.category === 'MANUAL' && topic.prototype_day_index ? (
					<Text size="xs" color="dimmed">
						From the lesson of day {topic.prototype_day_index}
					</Text>
				) : null}
			</Stack>
		</Card>
	)
}

// ---------------------------------------------------------------------------
// The edit window
// ---------------------------------------------------------------------------

type EditState =
	| {
			kind: 'plain'
			title: string
			level: string
			intro: string
			questions: SpeakingQuestionInput[]
	  }
	| { kind: 'ielts'; values: IeltsFormValues }

const editStateOf = (topic: SpeakingDailyTopic): EditState =>
	topic.category === 'IELTS_TOPICS'
		? { kind: 'ielts', values: ieltsFormFromTopic(topic) }
		: {
				kind: 'plain',
				title: topic.title || '',
				level: topic.level || 'A2',
				intro: topic.intro || '',
				questions: questionsToInputs(topic.questions),
			}

export function SpeakingTopicEditModal({
	topic,
	onClose,
	onSaved,
}: {
	topic: SpeakingDailyTopic | null
	onClose: () => void
	onSaved: () => void
}) {
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const [state, setState] = useState<EditState | null>(null)
	const [showErrors, setShowErrors] = useState(false)

	useEffect(() => {
		setState(topic ? editStateOf(topic) : null)
		setShowErrors(false)
	}, [topic])

	const ieltsErrors = useMemo(
		() => (state?.kind === 'ielts' ? ieltsFormErrors(state.values) : {}),
		[state],
	)
	const plainTitleError =
		state?.kind === 'plain' && !state.title.trim() ? 'Title is required' : null
	const plainQuestionsError =
		state?.kind === 'plain' ? questionsError(state.questions) : null

	const save = async () => {
		if (!topic || !state) return
		const invalid =
			state.kind === 'ielts'
				? Object.keys(ieltsErrors).length > 0
				: Boolean(plainTitleError || plainQuestionsError)
		if (invalid) {
			setShowErrors(true)
			return
		}
		const body: SpeakingTopicEditPayload =
			state.kind === 'ielts'
				? ieltsFormToPayload(state.values)
				: {
						title: state.title.trim(),
						level: state.level,
						intro: state.intro.trim() || null,
						questions: inputsToQuestions(state.questions),
					}
		openLoadingContext()
		try {
			const response = await updateSpeakingTopic(topic.id, body)
			if (response?.data?.code === 200) {
				onSaved()
				openModal({ type: 'success', label: 'Topic has been updated' })
			}
		} catch (error) {
			// 409: a learner started a talk on it while the window was open. The server's words say so.
			openError(error)
		} finally {
			closeLoadingContext()
		}
	}

	return (
		<Modal
			opened={Boolean(topic)}
			onClose={onClose}
			title={
				topic
					? `Edit ${categoryLabel(topic.category)} topic, ${topic.date}`
					: ''
			}
			size={720}
			centered
		>
			{topic && state ? (
				<Stack>
					{state.kind === 'plain' ? (
						<>
							<TextInput
								withAsterisk
								label="Title"
								value={state.title}
								error={showErrors ? plainTitleError : null}
								onChange={(event) =>
									setState({ ...state, title: event.currentTarget.value })
								}
							/>
							<Select
								label="Level"
								data={
									topic.category === 'HOT_TOPICS' ? HOT_LEVELS : BASIC_LEVELS
								}
								value={state.level}
								onChange={(value) =>
									setState({ ...state, level: value || state.level })
								}
							/>
							<SpeakingLessonFields
								intro={state.intro}
								questions={state.questions}
								questionsError={showErrors ? plainQuestionsError : null}
								onIntroChange={(value) => setState({ ...state, intro: value })}
								onQuestionsChange={(value) =>
									setState({ ...state, questions: value })
								}
							/>
						</>
					) : (
						<>
							<TextInput
								withAsterisk
								label="Title"
								value={state.values.title}
								error={showErrors ? ieltsErrors.title : null}
								onChange={(event) =>
									setState({
										...state,
										values: {
											...state.values,
											title: event.currentTarget.value,
										},
									})
								}
							/>
							<TextInput
								withAsterisk
								label="Part 1 theme"
								value={state.values.part1_theme}
								error={showErrors ? ieltsErrors.part1_theme : null}
								onChange={(event) =>
									setState({
										...state,
										values: {
											...state.values,
											part1_theme: event.currentTarget.value,
										},
									})
								}
							/>
							<Textarea
								withAsterisk
								label="Part 1 questions"
								description="4 or 5 questions, one per line"
								autosize
								minRows={4}
								value={state.values.part1_questions}
								error={showErrors ? ieltsErrors.part1_questions : null}
								onChange={(event) =>
									setState({
										...state,
										values: {
											...state.values,
											part1_questions: event.currentTarget.value,
										},
									})
								}
							/>
							<Textarea
								withAsterisk
								label="Cue card"
								autosize
								minRows={2}
								value={state.values.cue_card}
								error={showErrors ? ieltsErrors.cue_card : null}
								onChange={(event) =>
									setState({
										...state,
										values: {
											...state.values,
											cue_card: event.currentTarget.value,
										},
									})
								}
							/>
							<Textarea
								withAsterisk
								label="Cue card bullets"
								description="3 or 4 bullets, one per line"
								autosize
								minRows={3}
								value={state.values.bullets}
								error={showErrors ? ieltsErrors.bullets : null}
								onChange={(event) =>
									setState({
										...state,
										values: {
											...state.values,
											bullets: event.currentTarget.value,
										},
									})
								}
							/>
							<TextInput
								withAsterisk
								label="Closing line of the card"
								value={state.values.explain}
								error={showErrors ? ieltsErrors.explain : null}
								onChange={(event) =>
									setState({
										...state,
										values: {
											...state.values,
											explain: event.currentTarget.value,
										},
									})
								}
							/>
							<Textarea
								withAsterisk
								label="Rounding off questions"
								description="2 questions, one per line"
								autosize
								minRows={2}
								value={state.values.rounding_off}
								error={showErrors ? ieltsErrors.rounding_off : null}
								onChange={(event) =>
									setState({
										...state,
										values: {
											...state.values,
											rounding_off: event.currentTarget.value,
										},
									})
								}
							/>
							<Text size="xs" color="dimmed">
								The preparation time (60 s) and the talk time (up to 120 s) are
								fixed by the exam format.
							</Text>
						</>
					)}
					<Text size="xs" color="dimmed">
						{topic.category === 'MANUAL'
							? 'This changes the topic of this date only. The lesson in the library is not touched.'
							: 'The text can be changed until the first learner starts a talk on it.'}
					</Text>
					<Group position="right">
						<Button variant="default" onClick={onClose}>
							Cancel
						</Button>
						<Button color="green" onClick={save}>
							Save topic
						</Button>
					</Group>
				</Stack>
			) : null}
		</Modal>
	)
}

// ---------------------------------------------------------------------------
// The viewer
// ---------------------------------------------------------------------------

/**
 * The topics of one date (UD-671, design G): the three sets with their questions, the IELTS cue card and
 * the sources, with Edit and Regenerate. Regenerate is for a date from today on and a set no learner has
 * started; Edit is for a set no learner has started. The server decides, the buttons only follow it.
 */
function SpeakingDailyTopics({ courseId }: { courseId: string }) {
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const [date, setDate] = useState(() => dayjs().format(DATE_FORMAT))
	const [data, setData] = useState<SpeakingTopicsOfDate | null>(null)
	const [loading, setLoading] = useState(false)
	const [editing, setEditing] = useState<SpeakingDailyTopic | null>(null)

	const load = useCallback(async () => {
		if (!courseId || !isDateText(date)) {
			setData(null)
			return
		}
		setLoading(true)
		try {
			const response = await getSpeakingTopicsOfDate(courseId, date)
			setData(unwrapObject<SpeakingTopicsOfDate>(response))
		} catch (error) {
			openError(error)
			setData(null)
		} finally {
			setLoading(false)
		}
	}, [courseId, date, openError])

	useEffect(() => {
		load()
	}, [load])

	const regenerate = useCallback(
		(category?: 'HOT_TOPICS' | 'IELTS_TOPICS') => {
			const what = category ? categoryLabel(category) : 'Hot topic and IELTS'
			openModal({
				type: 'confirm',
				label: `Write the ${what} set${category ? '' : 's'} of ${date} again? The current text is replaced. This asks the AI and takes up to a minute.`,
				handleConfirm: async () => {
					openLoadingContext()
					try {
						const response = await regenerateSpeakingTopics({
							course_id: courseId,
							date,
							...(category ? { category } : {}),
						})
						if (response?.data?.code === 200) {
							await load()
							const result = unwrapObject<{
								created?: number
								missing?: number
							}>(response)
							openModal({
								type: result?.missing ? 'warning' : 'success',
								label: result?.missing
									? `${result.created || 0} written, ${result.missing} still missing. Try again, or the next run fills the gap.`
									: `${result?.created || 0} topic${result?.created === 1 ? '' : 's'} written`,
							})
						}
					} catch (error) {
						openError(error)
					} finally {
						closeLoadingContext()
					}
				},
			})
		},
		[
			closeLoadingContext,
			courseId,
			date,
			load,
			openError,
			openLoadingContext,
			openModal,
		],
	)

	const byCategory = useMemo(() => {
		const map = new Map<string, SpeakingDailyTopic[]>()
		for (const topic of data?.topics || []) {
			map.set(topic.category, [...(map.get(topic.category) || []), topic])
		}
		return map
	}, [data])

	const isPast = Boolean(data?.is_past)
	const anyRegenerable = (data?.topics || []).some(
		(topic) => topic.can_regenerate,
	)
	const emptyAiSet = ['HOT_TOPICS', 'IELTS_TOPICS'].some(
		(category) => !byCategory.has(category),
	)
	const canRegenerateAll = !isPast && (anyRegenerable || emptyAiSet)

	if (!courseId) {
		return (
			<Text size="sm" color="orange">
				There is no Speaking course yet.
			</Text>
		)
	}

	return (
		<Stack spacing="md">
			<Flex gap={10} align="flex-end" wrap="wrap" justify="space-between">
				<Group spacing={6} align="flex-end">
					<Tooltip label="Previous day" withinPortal>
						<ActionIcon
							variant="default"
							size={36}
							aria-label="Previous day"
							onClick={() => setDate(shiftDate(date, -1))}
						>
							<IconChevronLeft size={18} />
						</ActionIcon>
					</Tooltip>
					<TextInput
						type="date"
						label="Date"
						aria-label="Date"
						value={date}
						onChange={(event) => setDate(event.currentTarget.value)}
						sx={{ width: 170 }}
					/>
					<Tooltip label="Next day" withinPortal>
						<ActionIcon
							variant="default"
							size={36}
							aria-label="Next day"
							onClick={() => setDate(shiftDate(date, 1))}
						>
							<IconChevronRight size={18} />
						</ActionIcon>
					</Tooltip>
					<Button
						variant="default"
						onClick={() => setDate(dayjs().format(DATE_FORMAT))}
					>
						Today
					</Button>
				</Group>
				<Tooltip
					label={
						isPast
							? 'A past date cannot be regenerated'
							: 'Write the Hot topic and IELTS sets of this date again'
					}
					withinPortal
				>
					<span>
						<Button
							leftIcon={<IconRefresh size={16} />}
							disabled={!canRegenerateAll || loading}
							onClick={() => regenerate()}
							sx={{
								background: color.CmsPrimary,
								color: '#fff',
								':hover': { background: `${color.CmsPrimary} !important` },
							}}
						>
							Regenerate Hot and IELTS
						</Button>
					</span>
				</Tooltip>
			</Flex>

			{!isDateText(date) ? (
				<Text size="sm" color="orange">
					Pick a date.
				</Text>
			) : (
				<Text size="xs" color="dimmed">
					{loading
						? 'Loading...'
						: data
							? `${data.topics.length} of 3 sets for ${data.date}${data.is_past ? ' (past date: text can be viewed and edited, not regenerated)' : ''}`
							: ' '}
				</Text>
			)}

			<Stack spacing="md">
				{data
					? SPEAKING_CATEGORY_ORDER.map((category) => {
							const topics = byCategory.get(category) || []
							if (topics.length === 0) {
								return (
									<Card
										key={category}
										withBorder
										radius="md"
										padding="md"
										data-testid={`missing-${category}`}
										sx={{ borderStyle: 'dashed' }}
									>
										<Flex
											justify="space-between"
											align="center"
											gap={8}
											wrap="wrap"
										>
											<Group spacing={8}>
												<Badge
													color={CATEGORY_COLORS[category]}
													variant="light"
												>
													{categoryLabel(category)}
												</Badge>
												<Text size="sm" color="dimmed">
													No {categoryLabel(category)} set for this date yet.
												</Text>
											</Group>
											{category !== 'MANUAL' && !isPast ? (
												<Button
													size="xs"
													variant="light"
													color="orange"
													leftIcon={<IconRefresh size={14} />}
													onClick={() =>
														regenerate(
															category as 'HOT_TOPICS' | 'IELTS_TOPICS',
														)
													}
												>
													Generate
												</Button>
											) : null}
										</Flex>
									</Card>
								)
							}
							return topics.map((topic) => (
								<TopicCard
									key={topic.id}
									topic={topic}
									onEdit={setEditing}
									onRegenerate={(item) =>
										regenerate(item.category as 'HOT_TOPICS' | 'IELTS_TOPICS')
									}
								/>
							))
						})
					: null}
			</Stack>

			<SpeakingTopicEditModal
				topic={editing}
				onClose={() => setEditing(null)}
				onSaved={() => {
					setEditing(null)
					void load()
				}}
			/>
		</Stack>
	)
}

export default React.memo(SpeakingDailyTopics)
